import {
    createContext,
    useContext,
    useState,
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useCallback,
} from 'react'
import { useSnack } from './snack_context'
import { useLoading } from './loading_context'
import { createGroup, getAllGroups } from '../services/group'
import { createSubGroup, getSubGroupsByGroupId } from '../services/subgroup'
import { createMark, getAllMarks } from '../services/mark'
import { createModel, getModelsByMarkId } from '../services/model'
import {
    BarcodeAlreadyInUseError,
    CreateProductInterface,
    createProduct,
    getProductById,
    massEditProducts,
    updateProduct,
} from '../services/product'
import { useNavigate } from 'react-router-dom'
import { AccountInfo, useAccount } from './account_context'
import { ImageInterface } from '../pages/Accounts/Register'
import { ClientInfo } from './client_context'
import { CoinInfo, useCoin } from './coin_context'
import { BranchInfo } from './branch_context'
import { CashierInfo } from './cashier_context'
import { ExchangeInfo } from './buy_context'
import { SupplierInfo } from './supplier_context'
import { ProductInDelivery } from './sale_context'
import { SystemBranchEnum, useConfigs } from './configs_context'
const currency = require('currency.js')

export enum StockLevelEnum {
    LOW = 0,
    MEDIUM = 1,
    HIGH = 2,
    NEGATIVE = 3,
    POSITIVE = 4,
}

export const StockLevelLabel: Record<number, string> = {
    0: 'Stock baixo',
    1: 'Stock médio',
    2: 'Stock alto',
    3: 'Stock negativo',
    4: 'Stock positivo',
}

export enum ProductOrderEnum {
    Name = 'name',
    Localization = 'localization',
    Price = 'price',
    InternalCode = 'internalCode',
}

export const ProductOrderLabel: Record<string, string> = {
    name: 'Nombre',
    localization: 'Localización',
    price: 'Precio',
    internalCode: 'Código Interno',
}

export interface Group {
    id: number
    name: string
}

export interface SubGroup {
    id: number
    name: string
    groupId: number
}

export interface Mark {
    id: number
    name: string
}

export interface Model {
    id: number
    name: string
    markId: number
}

export enum IvaEnum {
    Exento = 0,
    Cinco = 5,
    Dez = 10,
    Cem = 100,
}

export enum UnitType {
    Unit = 'Un',
    Kg = 'Kg',
    'Mt/m2' = 'Mt/m2',
}

export enum BranchStockType {
    Normal = 0,
    Reserved = 1,
}

export const BranchStockTypeLabel: Record<number, string> = {
    0: 'Normal',
    1: 'Reservado',
}

export interface BranchStock {
    id: number
    branchId: number
    stock: number
    unitType: string
    minStock: number | null
    idealStock: number | null
    type: number
}

export interface NamedPrice {
    name: string
    price: number
    porcentGain: number
    clients: ClientInfo[]
}

export enum BarcodeTypeEnum {
    Barcode = 0,
    Internal = 1,
}

export interface ProductInfo {
    id?: number
    name: string
    aplication: string | null
    selectedGroup: Group | null
    selectedSubGroup: SubGroup | null
    selectedMark: Mark | null
    selectedModel: Model | null
    minStock: number | null
    idealStock: number | null
    stock: number | null
    observation: string | null
    barcodes: string[]
    internalCodes: string[]
    unitType: string
    buyPrice: number
    cifPrice: number
    avgBuyPrice: number
    avgCifPrice: number
    salePrice: number
    porcentGain: number
    qntPrice: {
        price: number
        qnt: number
    }[]
    namedPrice: NamedPrice[]
    image: ImageInterface | null
    iva: number
    proporcaoIva: number
    showIva: boolean
    sellWithoutStock: boolean
    isAtive: boolean
    autoSum: boolean
    autoPrice: boolean
    weight?: number
    branchStocks?: BranchStock[]
    supplier: SupplierInfo | null
    branch: BranchInfo | null
    localizations: {
        value: string
        branchId?: number
    }[]
    productsStockAfterOperation: {
        product: ProductInfo | null
        qnt: number
    }[]
    parsedName?: string
    noteProduct: ProductInfo | null
    obrigatoryIva: boolean
    realWeight: number
    ncm?: string | null
    coin?: CoinInfo
    withoutComission: boolean
    branches: (BranchInfo | null)[]
}

export interface ProductHistoricInfo {
    id: number
    operationId: number
    date: string
    hour: string
    value: number
    qnt: number
    type:
        | 'entrada'
        | 'saida'
        | 'carga_estoque'
        | 'transferencia_estoque'
        | 'nota_credito'
        | 'carga_estoque_apos_operacao'
    isSummerTime: boolean
    agentName: string | null
    branchName: string | null
    productName: string
    coin?: CoinInfo
    exchange: ExchangeInfo | null
    cifPrice: number
    avgBuyPrice: number
    avgCifPrice: number
    employeeName: string | null
}

export interface ProductFromExcel {
    product: ProductInfo
    qnt: number
    price: number
}

export interface NotFoundProductFromExcel {
    code: string
    name: string
    qnt: number
    price: number
}

export interface CreateProductInfo extends ProductInfo {
    groups: Group[]
    subGroups: SubGroup[] | null
    marks: Mark[]
    models: Model[] | null
    isEditing: boolean
    step: number
}

export interface ProductErrorInfo {
    name: string
    aplication: string
    selectedGroup: string
    selectedSubGroup: string
    selectedMark: string
    selectedModel: string
    stock: string
    minStock: string
    idealStock: string
    observation: string
    barcodes: string[]
    internalCodes: string[]
    buyPrice: string
    salePrice: string
    porcentGain: string
    qntPrice: string[]
}

export interface ProductInLoadStock {
    id: number
    qnt: number
    negativeQnt: number
    product: ProductInfo
}

export interface ProductInLoadSerial {
    id: number
    serial: string
    isDelivered: boolean
    product?: ProductInfo
    loadSerial?: LoadSerialInfo
    isAvailable?: boolean
    productInDelivery?: ProductInDelivery[]
}

export interface ProductInLoadLote {
    id: number
    lote: string
    quantidade: number
    deliveredQnt: number
    expDate: string | null
    product?: ProductInfo
    loadLote?: LoadLoteInfo
    isAvailable?: boolean
    productInDelivery?: ProductInDelivery[]
}

export interface LoadStockInfo {
    id?: number
    products: ProductInLoadStock[]
    branch: BranchInfo | null
    date?: string
    hour?: string
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
    isSummerTime?: boolean
    productCount?: number
    annotation?: string | null
    status: number
}

export interface LoadSerialInfo {
    id?: number
    products: ProductInLoadSerial[]
    branch: BranchInfo | null
    date?: string
    hour?: string
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
    isSummerTime?: boolean
    buyId: number | null
}

export interface LoadLoteInfo {
    id?: number
    products: ProductInLoadLote[]
    branch: BranchInfo | null
    date?: string
    hour?: string
    cashier?: Partial<CashierInfo>
    account?: Partial<AccountInfo>
    isSummerTime?: boolean
    buyId: number | null
}

export interface ProductFilters {
    page?: number
    rows?: number
    rowsCount: number
    groups: Group[]
    subgroups: SubGroup[]
    marks: Mark[]
    models: Model[]
    suppliers: SupplierInfo[]
    branches: BranchInfo[]
    stockLevel: number | null
    orderBy: string | null
}

export enum LoadStockStatus {
    Created = 0,
    Canceled = 1,
}

export const LoadStockStatusLabel: Record<number, string> = {
    0: 'Creado',
    1: 'Cancelado',
}

const productDefault: CreateProductInfo = {
    name: '',
    aplication: '',
    groups: [],
    selectedGroup: null,
    subGroups: null,
    selectedSubGroup: null,
    marks: [],
    selectedMark: null,
    models: null,
    selectedModel: null,
    minStock: 0,
    idealStock: 0,
    stock: 0,
    observation: '',
    barcodes: [''],
    internalCodes: [''],
    unitType: UnitType.Unit,
    buyPrice: 0,
    cifPrice: 0,
    avgBuyPrice: 0,
    avgCifPrice: 0,
    salePrice: 0,
    porcentGain: 0,
    qntPrice: [{ price: 0, qnt: 0 }],
    namedPrice: [],
    image: null,
    step: 1,
    iva: IvaEnum.Dez,
    proporcaoIva: 100,
    showIva: false,
    sellWithoutStock: false,
    isEditing: false,
    isAtive: true,
    autoSum: true,
    autoPrice: false,
    supplier: null,
    branch: null,
    localizations: [{ value: '' }],
    productsStockAfterOperation: [{ product: null, qnt: 0 }],
    noteProduct: null,
    obrigatoryIva: false,
    realWeight: 0,
    withoutComission: false,
    branches: [null],
}

const productErrorDefault: ProductErrorInfo = {
    name: '',
    aplication: '',
    selectedGroup: '',
    selectedSubGroup: '',
    selectedMark: '',
    selectedModel: '',
    stock: '',
    minStock: '',
    idealStock: '',
    observation: '',
    barcodes: [''],
    internalCodes: [''],
    buyPrice: '',
    salePrice: '',
    porcentGain: '',
    qntPrice: [''],
}

export const ProductHistoricTypeLabel: Record<string, string> = {
    entrada: 'Compra',
    saida: 'Venta',
    carga_estoque: 'Carga de stock',
    transferencia_estoque: 'Transferência de stock',
    nota_credito: 'Nota de crédito',
    carga_estoque_apos_operacao: 'Carga stock após operacción',
}

interface MassEditSelectedField {
    field: string
    name: string
    apiName: string
    selected: boolean
    hide?: boolean
}

const massEditSelectedFieldsDefault: MassEditSelectedField[] = [
    { field: 'Nombre', name: 'name', apiName: 'nome', selected: false },
    {
        field: 'Solicitud',
        name: 'aplication',
        apiName: 'aplicacao',
        selected: false,
    },
    {
        field: 'Grupo',
        name: 'selectedGroup',
        apiName: 'id_grupo',
        selected: false,
    },
    {
        field: 'Subgrupo',
        name: 'selectedSubGroup',
        apiName: 'id_sub_grupo',
        selected: false,
    },
    {
        field: 'Marca',
        name: 'selectedMark',
        apiName: 'id_marca',
        selected: false,
    },
    {
        field: 'Modelo',
        name: 'selectedModel',
        apiName: 'id_modelo',
        selected: false,
    },
    {
        field: 'Proveedor',
        name: 'supplier',
        apiName: 'id_fornecedor',
        selected: false,
    },
    {
        field: 'Observación',
        name: 'observation',
        apiName: 'observacao',
        selected: false,
    },
    {
        field: 'Producto factura',
        name: 'noteProduct',
        apiName: 'id_produto_fatura',
        selected: false,
    },
    { field: 'NCM', name: 'ncm', apiName: 'ncm', selected: false },
    { field: 'Peso', name: 'realWeight', apiName: 'peso', selected: false },
    {
        field: 'Sucursal',
        name: 'branch',
        apiName: 'id_filial_product',
        selected: false,
    },
    { field: 'Moneda', name: 'coin', apiName: 'id_moeda', selected: false },
    {
        field: 'Códigos de barra',
        name: 'barcodes',
        apiName: 'codigo_barras',
        selected: false,
        hide: true,
    },
    {
        field: 'Códigos internos',
        name: 'internalCodes',
        apiName: 'codigo_interno',
        selected: false,
        hide: true,
    },
    {
        field: 'Precio de compra',
        name: 'buyPrice',
        apiName: 'valor_compra',
        selected: false,
        hide: true,
    },
    {
        field: 'Porcentaje de ganancia',
        name: 'porcentGain',
        apiName: 'porc_lucro',
        selected: false,
        hide: true,
    },
    {
        field: 'Precio de venta',
        name: 'salePrice',
        apiName: 'preço',
        selected: false,
        hide: true,
    },
    {
        field: 'Lista de precios',
        name: 'namedPrice',
        apiName: 'preço_nome',
        selected: false,
        hide: true,
    },
    {
        field: 'Unidad de medida',
        name: 'unitType',
        apiName: 'unit_measurement',
        selected: false,
    },
    {
        field: 'Localizaciónes',
        name: 'localizations',
        apiName: 'localizations',
        selected: false,
        hide: true,
    },
    {
        field: 'Carga pós venta',
        name: 'productsStockAfterOperation',
        apiName: 'productsStockAfterOperation',
        selected: false,
        hide: true,
    },
    {
        field: 'Stock mínimo',
        name: 'minStock',
        apiName: 'stock_minimo',
        selected: false,
    },
    {
        field: 'Stock ideal',
        name: 'idealStock',
        apiName: 'stock_ideal',
        selected: false,
    },
    { field: 'IVA', name: 'iva', apiName: 'valor_iva', selected: false },
    {
        field: 'Proporção do IVA',
        name: 'proporcaoIva',
        apiName: 'proporcao_iva',
        selected: false,
    },
    {
        field: 'Permitir venta sin stock',
        name: 'sellWithoutStock',
        apiName: 'flag_sem_estoque',
        selected: false,
    },
    {
        field: 'Producto activo',
        name: 'isAtive',
        apiName: 'flag_ativo',
        selected: false,
    },
    {
        field: 'Autosuma',
        name: 'autoSum',
        apiName: 'flag_autosoma',
        selected: false,
    },
    {
        field: 'Autoprecio',
        name: 'autoPrice',
        apiName: 'flag_autopreco',
        selected: false,
    },
    {
        field: 'IVA obrigatório',
        name: 'obrigatoryIva',
        apiName: 'flag_iva_obrigatorio',
        selected: false,
    },
    {
        field: 'Producto sin comision',
        name: 'withoutComission',
        apiName: 'flag_sem_comissao',
        selected: false,
    },
    {
        field: 'Imagen del producto',
        name: 'image',
        apiName: 'path_imagem',
        selected: false,
        hide: true,
    },
    {
        field: 'Filiais',
        name: 'branches',
        apiName: 'filiais',
        selected: false,
        hide: true,
    },
]

export const productFiltersDefault: ProductFilters = {
    page: 1,
    rows: 10,
    rowsCount: 0,
    groups: [],
    subgroups: [],
    marks: [],
    models: [],
    branches: [],
    suppliers: [],
    stockLevel: null,
    orderBy: null,
}

export interface ProductContextInterface {
    productInfo: CreateProductInfo
    setProductInfo: Dispatch<SetStateAction<CreateProductInfo>>
    productErrorInfo: ProductErrorInfo
    setProductErrorInfo: Dispatch<SetStateAction<ProductErrorInfo>>
    validate(): boolean
    startNewProduct(): void
    startMassEdit(): void
    editProduct(id: number): Promise<void>
    handleCreateNewGroup(name: string): Promise<void>
    handleCreateNewSubGroup(name: string, groupId: number): Promise<void>
    handleCreateNewMark(name: string): Promise<void>
    handleCreateNewModel(name: string, groupId: number): Promise<void>
    handleCreateNewProduct(): Promise<ProductInfo | null>
    handleMassEdit(): Promise<void>
    products: ProductInfo[]
    setProducts: Dispatch<SetStateAction<ProductInfo[]>>
    selectedProducts: ProductInfo[]
    setSelectedProducts: Dispatch<SetStateAction<ProductInfo[]>>
    filters: ProductFilters
    setFilters: Dispatch<SetStateAction<ProductFilters>>
    search: string
    setSearch: Dispatch<SetStateAction<string>>
    massEditSelectedFields: MassEditSelectedField[]
    setMassEditSelectedFields: Dispatch<SetStateAction<MassEditSelectedField[]>>
    mountCreateProductRequest: (
        product: CreateProductInfo
    ) => CreateProductInterface
    priceList: string[]
    setPriceList: Dispatch<SetStateAction<string[]>>
}

const productContext = createContext<ProductContextInterface>({
    productInfo: productDefault,
    setProductInfo: () => {},
    productErrorInfo: productErrorDefault,
    setProductErrorInfo: () => {},
    validate: () => {
        return false
    },
    startNewProduct: () => {},
    startMassEdit: () => {},
    editProduct: async () => {},
    handleCreateNewGroup: async () => {},
    handleCreateNewSubGroup: async () => {},
    handleCreateNewMark: async () => {},
    handleCreateNewModel: async () => {},
    handleCreateNewProduct: async () => {
        return null
    },
    handleMassEdit: async () => {},
    products: [],
    setProducts: () => {},
    selectedProducts: [],
    setSelectedProducts: () => {},
    filters: JSON.parse(JSON.stringify(productFiltersDefault)),
    setFilters: () => {},
    search: '',
    setSearch: () => {},
    massEditSelectedFields: [],
    setMassEditSelectedFields: () => {},
    mountCreateProductRequest: () => {
        throw new Error()
    },
    priceList: [],
    setPriceList: () => {},
})

export function useProduct() {
    return useContext(productContext)
}

interface Props {
    children: React.ReactNode
}

export function ProductProvider({ children }: Props) {
    const snack = useSnack()
    const loading = useLoading()
    const [productInfo, setProductInfo] =
        useState<CreateProductInfo>(productDefault)
    const [productErrorInfo, setProductErrorInfo] =
        useState<ProductErrorInfo>(productErrorDefault)
    const navigate = useNavigate()
    const { coins } = useCoin()
    const { configs } = useConfigs()
    const { accountInfo } = useAccount()
    const [products, setProducts] = useState<ProductInfo[]>([])
    const [selectedProducts, setSelectedProducts] = useState<ProductInfo[]>([])
    const [filters, setFilters] = useState<ProductFilters>({
        page: 1,
        rows: 10,
        rowsCount: 0,
        groups: [],
        subgroups: [],
        marks: [],
        models: [],
        branches: [],
        suppliers: [],
        stockLevel: null,
        orderBy: null,
    })
    const [search, setSearch] = useState('')
    const [massEditSelectedFields, setMassEditSelectedFields] = useState<
        MassEditSelectedField[]
    >(JSON.parse(JSON.stringify(massEditSelectedFieldsDefault)))
    const [priceList, setPriceList] = useState<string[]>([])

    const canUpdateProduct =
        accountInfo?.isMaster ||
        accountInfo?.rules.findIndex(
            (r) =>
                r.group.toLowerCase() === 'produtos' &&
                r.name.toLowerCase() === 'atualizar'
        ) !== -1

    useEffect(() => {
        window.scroll(0, 0)
    }, [productInfo.step])

    useEffect(() => {
        if (!productInfo.selectedGroup) return
        loadSubGroups(productInfo.selectedGroup.id)
    }, [productInfo.selectedGroup])

    useEffect(() => {
        if (!productInfo.selectedMark) return
        loadModels(productInfo.selectedMark.id)
    }, [productInfo.selectedMark])

    const startNewProduct = useCallback(() => {
        setProductInfo((prev) => ({
            ...JSON.parse(JSON.stringify(productDefault)),
            groups: prev.groups,
            marks: prev.marks,
            namedPrice: configs.priceComission
                .filter((p) => p.name)
                .map((p) => ({
                    name: p.name,
                    price: 0,
                    porcentGain: p.porcentGain,
                    clients: [],
                })),
        }))
        setProductErrorInfo({ ...productErrorDefault })
    }, [configs])

    const startMassEdit = useCallback(() => {
        setProductInfo((prev) => ({
            ...JSON.parse(JSON.stringify(productDefault)),
            groups: prev.groups,
            marks: prev.marks,
            namedPrice: configs.priceComission
                .filter((p) => p.name)
                .map((p) => ({
                    name: p.name,
                    price: 0,
                    porcentGain: p.porcentGain,
                    clients: [],
                })),
        }))
        setProductErrorInfo({ ...productErrorDefault })
        setMassEditSelectedFields(
            JSON.parse(JSON.stringify(massEditSelectedFieldsDefault))
        )
    }, [configs])

    const editProduct = useCallback(
        async (id: number) => {
            try {
                const product = await getProductById(id, coins)

                setProductInfo((prev) => ({
                    ...product,
                    groups: prev.groups,
                    marks: prev.marks,
                    subGroups: prev.subGroups,
                    models: prev.models,
                    step: 1,
                    isEditing: true,
                    qntPrice: product.qntPrice.length
                        ? product.qntPrice
                        : JSON.parse(JSON.stringify(productDefault.qntPrice)),
                    localizations: product.localizations.length
                        ? product.localizations
                        : JSON.parse(
                              JSON.stringify(productDefault.localizations)
                          ),
                    productsStockAfterOperation: product
                        .productsStockAfterOperation.length
                        ? product.productsStockAfterOperation
                        : JSON.parse(
                              JSON.stringify(
                                  productDefault.productsStockAfterOperation
                              )
                          ),
                    cifPrice: currency(product.buyPrice).add(product.cifPrice)
                        .value,
                }))
                setProductErrorInfo({ ...productErrorDefault })
            } catch (error) {
                console.log(error)
                snack.error('Produto não encontrado')
                throw new Error('Product not found')
            }
        },
        [coins, snack]
    )

    const loadGroups = useCallback(async () => {
        loading.show()
        try {
            const groups = await getAllGroups()
            setProductInfo((prev) => ({
                ...prev,
                groups: groups,
            }))
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }, [loading, snack])

    const loadSubGroups = useCallback(
        async (id: number) => {
            loading.show()
            try {
                const subGroups = await getSubGroupsByGroupId(id)
                setProductInfo((prev) => ({
                    ...prev,
                    subGroups: subGroups,
                }))
            } catch (error) {
                snack.connectionFail(error)
            }
            loading.hide()
        },
        [loading, snack]
    )

    const loadMarks = useCallback(async () => {
        loading.show()
        try {
            const marks = await getAllMarks()
            setProductInfo((prev) => ({
                ...prev,
                marks: marks,
            }))
        } catch (error) {
            snack.connectionFail(error)
        }
        loading.hide()
    }, [loading, snack])

    const loadModels = useCallback(
        async (id: number) => {
            loading.show()
            try {
                const models = await getModelsByMarkId(id)
                setProductInfo((prev) => ({
                    ...prev,
                    models: models,
                }))
            } catch (error) {
                snack.connectionFail(error)
            }
            loading.hide()
        },
        [loading, snack]
    )

    const handleCreateNewGroup = useCallback(
        async (name: string) => {
            if (!name) return
            loading.show()
            try {
                const newGroup = await createGroup(name)
                console.log(newGroup)
                const newGroups = [...productInfo.groups]
                newGroups.push(newGroup)
                setProductInfo((prev) => ({
                    ...prev,
                    groups: newGroups,
                    selectedGroup: newGroup,
                }))
            } catch (error) {
                snack.connectionFail(error)
            }
            loading.hide()
        },
        [loading, snack, productInfo.groups]
    )

    const handleCreateNewSubGroup = useCallback(
        async (name: string, groupId: number) => {
            if (!name) return
            loading.show()
            try {
                const newSubGroup = await createSubGroup(name, groupId)
                const newSubGroups = productInfo.subGroups
                    ? [...productInfo.subGroups]
                    : []
                newSubGroups.push(newSubGroup)
                setProductInfo((prev) => ({
                    ...prev,
                    subGroups: newSubGroups,
                    selectedSubGroup: newSubGroup,
                }))
            } catch (error) {
                snack.connectionFail(error)
            }
            loading.hide()
        },
        [loading, snack, productInfo.subGroups]
    )

    const handleCreateNewMark = useCallback(
        async (name: string) => {
            if (!name) return
            loading.show()
            try {
                const newMark = await createMark(name)
                const newMarks = [...productInfo.marks]
                newMarks.push(newMark)
                setProductInfo((prev) => ({
                    ...prev,
                    marks: newMarks,
                    selectedMark: newMark,
                }))
            } catch (error) {
                snack.connectionFail(error)
            }
            loading.hide()
        },
        [loading, snack, productInfo.marks]
    )

    const handleCreateNewModel = useCallback(
        async (name: string, markId: number) => {
            if (!name) return
            loading.show()
            try {
                const newModel = await createModel(name, markId)
                const newModels = productInfo.models
                    ? [...productInfo.models]
                    : []
                newModels.push(newModel)
                setProductInfo((prev) => ({
                    ...prev,
                    models: newModels,
                    selectedModel: newModel,
                }))
            } catch (error) {
                snack.connectionFail(error)
            }
            loading.hide()
        },
        [loading, snack, productInfo.models]
    )

    const mountCreateProductRequest = useCallback(
        (productInfo: CreateProductInfo): CreateProductInterface => {
            return {
                nome: productInfo.name,
                stock_minimo: Number(productInfo.minStock) || null,
                stock_ideal: Number(productInfo.idealStock) || null,
                stock_atual: !productInfo.isEditing
                    ? Number(productInfo.stock) || null
                    : undefined,
                aplicacao: productInfo.aplication || null,
                observacao: productInfo.observation || null,
                id_grupo: productInfo.selectedGroup?.id ?? null,
                id_sub_grupo: productInfo.selectedSubGroup?.id ?? null,
                id_marca: productInfo.selectedMark?.id ?? null,
                id_modelo: productInfo.selectedModel?.id ?? null,
                codigo_barras: productInfo.barcodes.filter(
                    (barcode) => barcode
                ),
                codigo_interno: productInfo.internalCodes.filter(
                    (internalCode) => internalCode
                ),
                path_imagem: productInfo.image?.content ?? null,
                valor_iva:
                    productInfo.iva === IvaEnum.Exento
                        ? 0
                        : productInfo.iva === IvaEnum.Cinco
                          ? 5
                          : 10,
                proporcao_iva: productInfo.proporcaoIva ?? 100,
                flag_ativo: productInfo.isAtive ? 1 : 0,
                flag_autosoma: productInfo.autoSum ? 1 : 0,
                flag_autopreco: productInfo.autoPrice ? 1 : 0,
                flag_controlar_estoque: 1,
                flag_imprimir_fatura: productInfo.showIva ? 1 : 0,
                flag_sem_estoque: productInfo.sellWithoutStock ? 1 : 0,
                unit_measurement: productInfo.unitType,
                preço: productInfo.qntPrice
                    .filter((qntPrice) => qntPrice.price && qntPrice.qnt)
                    .map((qntPrice) => {
                        return {
                            valor: qntPrice.price,
                            quantidade: Number(qntPrice.qnt),
                        }
                    })
                    .concat([
                        {
                            quantidade: 1,
                            valor: productInfo.salePrice,
                        },
                    ]),
                preço_nome: productInfo.namedPrice.map((np) => ({
                    nome: np.name,
                    valor: np.price,
                    porc_lucro: np.porcentGain,
                    clientes: np.clients.map((c) => c.id as number),
                })),
                valor_compra: productInfo.buyPrice,
                porc_lucro: productInfo.porcentGain,
                id_fornecedor: productInfo.supplier?.id ?? null,
                id_produto_fatura: productInfo.noteProduct?.id ?? null,
                id_filial_product:
                    !accountInfo?.branch &&
                    configs.systemBranch === SystemBranchEnum.Branch
                        ? productInfo.branch?.id ?? null
                        : undefined,
                flag_iva_obrigatorio: Number(productInfo.obrigatoryIva),
                ncm: productInfo.ncm ?? null,
                id_moeda: productInfo.coin?.id ?? 0,
                localizations: productInfo.localizations
                    .filter((l) => l.value)
                    .map((l) => ({
                        valor: l.value,
                        id_filial: l.branchId ?? null,
                    })),
                productsStockAfterOperation:
                    productInfo.productsStockAfterOperation
                        .filter((v) => v.product)
                        .map((v) => ({
                            id_produto_carga: v.product?.id as number,
                            quantidade_produto: v.qnt,
                        })),
                peso: productInfo.realWeight,
                flag_sem_comissao: Number(productInfo.withoutComission),
                filiais: productInfo.branches
                    .filter((b) => b)
                    .map((branch) => branch!.id),
            }
        },
        [accountInfo, configs]
    )

    const handleCreateNewProduct =
        useCallback(async (): Promise<ProductInfo | null> => {
            let productId: number | null = null
            loading.show()
            try {
                const requestInfo = mountCreateProductRequest(productInfo)

                if (configs.isCifPriceEditable) {
                    requestInfo.preco_cif = currency(
                        productInfo.cifPrice
                    ).subtract(productInfo.buyPrice).value
                }

                if (!productInfo.isEditing) {
                    const id = await createProduct(requestInfo)
                    productId = id
                    snack.success('Producto criado com sucesso!')
                } else if (productInfo.id) {
                    await updateProduct({
                        ...requestInfo,
                        id_produto: productInfo.id,
                    })
                    productId = productInfo.id
                    snack.success('Producto atualizado com sucesso!')
                }

                setSelectedProducts([])
            } catch (error: any) {
                if (error instanceof BarcodeAlreadyInUseError) {
                    const newProductErrorInfo = { ...productErrorDefault }
                    if (error.type === BarcodeTypeEnum.Barcode) {
                        productInfo.barcodes.forEach((barcode, i) => {
                            if (error.barcodes.includes(barcode)) {
                                newProductErrorInfo.barcodes[i] =
                                    'Código de barra já em uso'
                            }
                        })
                    }
                    if (error.type === BarcodeTypeEnum.Internal) {
                        productInfo.internalCodes.forEach((barcode, i) => {
                            if (error.barcodes.includes(barcode)) {
                                newProductErrorInfo.internalCodes[i] =
                                    'Código interno já em uso'
                            }
                        })
                    }
                    setProductErrorInfo({ ...newProductErrorInfo })
                    setTimeout(() => {
                        window.scroll(0, 0)
                    }, 100)
                }
                snack.connectionFail(error)
            }
            loading.hide()

            return productId ? await getProductById(productId, coins) : null
        }, [
            loading,
            snack,
            mountCreateProductRequest,
            configs,
            productInfo,
            coins,
        ])

    const handleMassEdit = useCallback(async (): Promise<void> => {
        loading.show()
        try {
            const requestInfo = mountCreateProductRequest(productInfo)

            for (const massEditSelectedField of massEditSelectedFields) {
                if (massEditSelectedField.selected) continue
                delete (requestInfo as any)[massEditSelectedField.apiName]
            }

            await massEditProducts({
                fields: requestInfo,
                filters: {
                    search: search.length ? search : undefined,
                    groups: filters.groups.map((g) => g.id),
                    subgroups: filters.subgroups.map((sg) => sg.id),
                    brands: filters.marks.map((m) => m.id),
                    models: filters.models.map((m) => m.id),
                    id_fornecedor: filters.suppliers.map((s) => s.id as number),
                    stockLevel: filters.stockLevel,
                    id_filial_filter: filters.branches[0]?.id,
                    orderBy: filters.orderBy as any,
                },
            })

            snack.success('Producto editados com sucesso!')
            navigate('/system/product')
        } catch (error: any) {
            if (error instanceof BarcodeAlreadyInUseError) {
                const newProductErrorInfo = { ...productErrorDefault }
                if (error.type === BarcodeTypeEnum.Barcode) {
                    productInfo.barcodes.forEach((barcode, i) => {
                        if (error.barcodes.includes(barcode)) {
                            newProductErrorInfo.barcodes[i] =
                                'Código de barra já em uso'
                        }
                    })
                }
                if (error.type === BarcodeTypeEnum.Internal) {
                    productInfo.internalCodes.forEach((barcode, i) => {
                        if (error.barcodes.includes(barcode)) {
                            newProductErrorInfo.internalCodes[i] =
                                'Código interno já em uso'
                        }
                    })
                }
                setProductErrorInfo({ ...newProductErrorInfo })
                setTimeout(() => {
                    window.scroll(0, 0)
                }, 100)
            }
            snack.connectionFail(error)
        }
        loading.hide()
    }, [
        loading,
        snack,
        mountCreateProductRequest,
        productInfo,
        massEditSelectedFields,
        search,
        filters,
        navigate,
    ])

    const validate = useCallback((): boolean => {
        let isOk = true
        const newProductErrorInfo = { ...productErrorDefault }

        if (!productInfo.isEditing || canUpdateProduct) {
            if (!productInfo.name) {
                newProductErrorInfo.name = 'Campo obrigatório'
                isOk = false
            }
            if (!productInfo.barcodes.filter((barcode) => barcode).length) {
                newProductErrorInfo.barcodes[0] = 'Campo obrigatório'
                isOk = false
            } else {
                productInfo.barcodes.forEach((barcode, i) => {
                    productInfo.barcodes.forEach((otherBarcode, j) => {
                        if (i === j || !barcode) return
                        if (barcode === otherBarcode) {
                            newProductErrorInfo.barcodes[i] =
                                'Os códigos de barra não podem ser iguais'
                            newProductErrorInfo.barcodes[j] =
                                'Os códigos de barra não podem ser iguais'
                            isOk = false
                        }
                    })
                })
            }

            if (productInfo.buyPrice <= 0) {
                newProductErrorInfo.buyPrice = 'Campo obrigatório'
                isOk = false
            }
            /* if (productInfo.porcentGain <= 0) {
                newProductErrorInfo.porcentGain = 'Campo obrigatório'
                isOk = false
            } */
            if (productInfo.salePrice <= 0) {
                newProductErrorInfo.salePrice = 'Campo obrigatório'
                isOk = false
            }
        }

        setProductErrorInfo({ ...newProductErrorInfo })
        return isOk
    }, [productInfo, canUpdateProduct])

    const value = useMemo(
        () => ({
            productInfo,
            setProductInfo,
            productErrorInfo,
            setProductErrorInfo,
            validate,
            startNewProduct,
            editProduct,
            handleCreateNewGroup,
            handleCreateNewSubGroup,
            handleCreateNewMark,
            handleCreateNewModel,
            handleCreateNewProduct,
            handleMassEdit,
            products,
            setProducts,
            selectedProducts,
            setSelectedProducts,
            filters,
            setFilters,
            search,
            setSearch,
            massEditSelectedFields,
            setMassEditSelectedFields,
            startMassEdit,
            mountCreateProductRequest,
            priceList,
            setPriceList,
        }),
        [
            productInfo,
            setProductInfo,
            productErrorInfo,
            setProductErrorInfo,
            validate,
            startNewProduct,
            editProduct,
            handleCreateNewGroup,
            handleCreateNewSubGroup,
            handleCreateNewMark,
            handleCreateNewModel,
            handleCreateNewProduct,
            handleMassEdit,
            products,
            setProducts,
            selectedProducts,
            setSelectedProducts,
            filters,
            setFilters,
            search,
            setSearch,
            massEditSelectedFields,
            setMassEditSelectedFields,
            startMassEdit,
            mountCreateProductRequest,
            priceList,
            setPriceList,
        ]
    )

    return (
        <productContext.Provider value={value}>
            {children}
        </productContext.Provider>
    )
}
